import {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Calendar, Popover, TextField} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';
import {Temporal} from 'temporal-polyfill';

import {isEventTimeInvalid} from '@/components/global/layout/EventBar/utils';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {FulfillmentDetailStrategy} from '@/graphql/types';
import useIdentity from '@/hooks/useIdentity';
import {useEventBarV2} from '../EventBarV2Provider';
import {useCommitEventBarFulfillment} from '../hooks/useCommitEventBarFulfillment';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';
import InfoBanner from '../InfoBanner';
import {type AdvanceDecision} from '../utils/askFlow';
import {focusAdjacentTabbable} from '../utils/fields';
import {buildWhenUpdateInput, formatWhenSummary} from '../utils/when';
import WhenTimeList from './WhenTimeList';

type WhenProps = {
  isCompressed?: boolean;
  isOpen: boolean;
  // Hands the bar on to the next field that still needs asking about, if any. A no-op on menu
  // pages, which edit in place behind the panel's own Update button.
  onAdvance: () => AdvanceDecision;
  onUpdateApplied?: () => void;
  setIsOpen: (open: boolean) => void;
};

const plainDateStringToDate = (isoDate: string): Date => {
  const {year, month, day} = Temporal.PlainDate.from(isoDate);
  return new Date(year, month - 1, day);
};

const dateToPlainDateString = (date: Date): string =>
  Temporal.PlainDate.from({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  }).toString();

type PopoverOpenChange = NonNullable<React.ComponentProps<typeof Popover.Root>['onOpenChange']>;
type SelectDateHandler = (
  date: Date | undefined,
  triggerDate: Date,
  modifiers: unknown,
  event: React.UIEvent,
) => void;

const When: React.FC<WhenProps> = ({
  isCompressed = false,
  isOpen,
  onAdvance,
  onUpdateApplied,
  setIsOpen,
}) => {
  const {t} = useTranslation('app-bar');
  const {isAdmin} = useIdentity();
  const {
    whenDate,
    whenTime,
    setWhenDate,
    setWhenTime,
    fulfillmentType,
    hasWhenEngaged,
    setHasWhenEngaged,
    timeZone,
  } = useEventBarV2();
  const {trackDateSelected, trackTimeSelected, trackWhenUpdate} = useEventBarV2Tracking();
  const {isOnMenuPage} = useStoreNextRouter();
  const {commitFulfillment} = useCommitEventBarFulfillment();

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLLabelElement>(null);
  // Set while we programmatically return focus to the input after Escape or a keyboard pick that
  // closes the bar.
  const isRestoringFocusRef = useRef(false);

  const [temporalNow, setTemporalNow] = useState<Temporal.ZonedDateTime>(
    Temporal.Now.instant().toZonedDateTimeISO(timeZone),
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const idSuffix = isCompressed ? 'compressed' : 'expanded';

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTemporalNow(Temporal.Now.instant().toZonedDateTimeISO(timeZone));
  }, [setTemporalNow, whenDate, whenTime, timeZone]);

  const isTimeInvalid = useMemo(
    () => (time: Temporal.PlainTime) => isEventTimeInvalid(time, whenDate, temporalNow, isAdmin),
    [whenDate, temporalNow, isAdmin],
  );
  const minCalendarDate = useMemo(
    () => (isAdmin ? undefined : plainDateStringToDate(temporalNow.toPlainDate().toString())),
    [isAdmin, temporalNow],
  );

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Auto-clear a selected time that has become invalid (e.g. after switching the date to today).
  useEffect(() => {
    if (whenTime && isTimeInvalid(Temporal.PlainTime.from(whenTime))) {
      setWhenTime(null);
    }
  }, [whenTime, isTimeInvalid, setWhenTime]);

  const summary = formatWhenSummary({
    whenDate,
    whenTime,
  });

  const isDelivery = fulfillmentType === FulfillmentDetailStrategy.Delivery;
  const containerId = `when-container-${idSuffix}`;
  const inputId = `when-input-${idSuffix}`;
  const panelId = `when-panel-${idSuffix}`;

  // A date without a time leaves the panel unfinished, so only a complete pair moves the bar on.
  const advanceIfComplete = (
    nextDate: string | null,
    nextTime: string | null,
    event: React.UIEvent,
  ) => {
    if (!buildWhenUpdateInput(nextDate, nextTime)) return;
    // A keyboard pick that closes the bar would drop focus to the page, so hand it back to the
    // input. Enter, Space, and screen reader activation report no click count; mouse picks do.
    if (onAdvance().kind === 'done' && event.detail === 0) {
      isRestoringFocusRef.current = true;
      inputRef.current?.focus();
    }
  };

  const handleUpdate = async () => {
    trackWhenUpdate();
    setIsUpdating(true);
    try {
      // Keep the panel open on a failed commit so the pending edit isn't silently lost.
      if ((await commitFulfillment()).ok) {
        setIsOpen(false);
        onUpdateApplied?.();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSelectDate: SelectDateHandler = (date, _triggerDate, _modifiers, event) => {
    const next = date ? dateToPlainDateString(date) : null;
    setWhenDate(next);
    trackDateSelected(next);

    if (!next) {
      // Clearing the date clears its time too — a time can't stand without a date.
      setWhenTime(null);
      return;
    }
    // A previously chosen time can become invalid on the new date (e.g. switching to today), in
    // which case the auto-clear effect nulls it — so don't count it toward closing the panel.
    const timeSurvives =
      whenTime != null &&
      !isEventTimeInvalid(Temporal.PlainTime.from(whenTime), next, temporalNow, isAdmin);
    advanceIfComplete(next, timeSurvives ? whenTime : null, event);
  };

  const handleSelectTime = (next: string | null, event: React.UIEvent) => {
    setWhenTime(next);
    trackTimeSelected(next);
    advanceIfComplete(whenDate, next, event);
  };

  const handleOpenChange: PopoverOpenChange = (next, eventDetails) => {
    // Presses inside the anchor container are part of the field itself, so they must not dismiss
    // the popup. Base UI replaces Radix's `onInteractOutside` with a cancelable `onOpenChange`.
    if (eventDetails.reason === 'outside-press') {
      const target = eventDetails.event.target as HTMLElement | null;
      if (target?.closest(`#${containerId}`)) {
        eventDetails.cancel();
        return;
      }
    }

    // Replaces `onEscapeKeyDown`; `finalFocus` below consumes this to restore the input.
    if (eventDetails.reason === 'escape-key') {
      isRestoringFocusRef.current = true;
    }

    setIsOpen(next);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (isOnMenuPage) return;
      event.preventDefault();
      // Leaving When by Enter settles it as an "Anytime" search. Say so explicitly rather than
      // relying on the input's blur: when there is no field left to ask about the bar just closes,
      // and focus stays put, so the placeholder would otherwise still read "Date & time".
      setHasWhenEngaged(true);
      onAdvance();
      return;
    }
    // While the panel is open, Base UI's popup focus guards have no trigger to hand focus back to,
    // so Tab would dead-end inside the portal. Move to the next field ourselves — the panel itself
    // is entered with ArrowDown below. Only suppress the keystroke if focus actually moved, so a
    // field with no adjacent tabbable falls through to the browser's own Tab rather than stranding
    // focus on a closed panel.
    if (event.key === 'Tab' && isOpen) {
      setIsOpen(false);
      if (focusAdjacentTabbable(event.currentTarget, event.shiftKey ? -1 : 1)) {
        event.preventDefault();
      }
      return;
    }
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    setIsOpen(true);
    requestAnimationFrame(() => {
      const panel = document.getElementById(panelId);
      const day =
        panel?.querySelector<HTMLElement>('[data-day][tabindex="0"]') ??
        panel?.querySelector<HTMLElement>('[data-day][tabindex]');
      day?.focus();
    });
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <label
        className={twJoin(
          'relative -left-2 flex flex-col justify-center rounded-full border-2 border-transparent py-2 transition-all duration-200 focus-within:z-10 hover:z-10 motion-reduce:transition-none',
          isCompressed ? 'h-[60px] px-5' : 'h-20 px-8',
        )}
        htmlFor={inputId}
        id={containerId}
        ref={anchorRef}
      >
        {!isCompressed && (
          <span className="text-nowrap font-semibold text-peppercorn-800">
            {t('eventBarV2.when.label')}
          </span>
        )}
        <TextField.Root className="border-none bg-transparent p-0 focus-within:ring-0 hover:bg-transparent">
          <TextField.Input
            aria-controls={panelId}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={t('eventBarV2.when.label')}
            role="combobox"
            className={twJoin(
              'cursor-pointer truncate p-0 focus:outline-none focus:ring-0',
              isCompressed && 'font-semibold placeholder:text-peppercorn-500',
            )}
            data-testid="when-input"
            id={inputId}
            onClick={() => setIsOpen(true)}
            onFocus={() => {
              // Escape-driven focus restoration flips this so it doesn't reopen the panel.
              if (isRestoringFocusRef.current) {
                isRestoringFocusRef.current = false;
                return;
              }
              setIsOpen(true);
            }}
            onBlur={() => setHasWhenEngaged(true)}
            onKeyDown={handleInputKeyDown}
            readOnly
            ref={inputRef}
            value={summary}
            placeholder={
              hasWhenEngaged ? t('eventBarV2.when.anytime') : t('eventBarV2.when.placeholder')
            }
          />
        </TextField.Root>
      </label>
      <Popover.Content
        align="start"
        anchor={anchorRef}
        aria-label={t('eventBarV2.when.label')}
        className="mt-2 max-h-[var(--available-height)] overflow-y-auto rounded-2xl border border-peppercorn-100 bg-white p-6 shadow-lg"
        data-event-bar-surface
        data-testid="when-panel"
        finalFocus={() => {
          // Base UI would return focus to the anchor; we manage it ourselves so the restore only
          // happens on Escape and never bounces the input's focus-opens behavior back open.
          if (!isRestoringFocusRef.current) return false;
          if (document.activeElement === inputRef.current) {
            // Escape was pressed from the input itself — nothing to restore.
            isRestoringFocusRef.current = false;
            return false;
          }
          // Leaves the guard set; the input's onFocus consumes and clears it.
          return inputRef.current;
        }}
        id={panelId}
        initialFocus={false}
        sideOffset={8}
      >
        <div className="flex items-stretch gap-4">
          <Calendar
            classNames={{
              day_button: 'data-[selected-single=true]:hover:bg-green-600',
              today: 'bg-transparent [&>button]:border-peppercorn-300 [&>button]:border-2',
            }}
            disabled={minCalendarDate ? {before: minCalendarDate} : undefined}
            mode="single"
            onSelect={handleSelectDate}
            selected={whenDate ? plainDateStringToDate(whenDate) : undefined}
            // Unlike the older event bars, this panel deliberately clamps navigation: `startMonth`
            // disables the month-back button and hides the leading outside-days, so past dates are
            // never shown. Keep it.
            startMonth={minCalendarDate}
            showYearSwitcher={false}
          />
          <div className="relative w-[200px]">
            <WhenTimeList
              fulfillmentType={fulfillmentType}
              idSuffix={idSuffix}
              isCompressed={isCompressed}
              isInvalid={isTimeInvalid}
              onSelect={handleSelectTime}
              value={whenTime}
            />
          </div>
        </div>
        {isDelivery && (
          <InfoBanner
            className="mt-3"
            data-testid="when-info-banner"
            text={t('eventBarV2.when.infoBanner')}
          />
        )}
        {isOnMenuPage && (
          <Button
            className="mt-4 w-full"
            data-testid="when-update-button"
            loading={isUpdating}
            onClick={handleUpdate}
          >
            {t('eventBarV2.update')}
          </Button>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default When;
